import { useState, useEffect, Fragment } from 'react'
import { startUseCredit } from '../../utils/creditSystem';

function VoiceSettingsModal({ onClose, onGenerate, id, clonedVoices = [] }) {
    const [selectedVoice, setSelectedVoice] = useState('');
    const [speed, setSpeed] = useState(1.0);
    const [volume, setVolume] = useState(1.0);
    const [pitch, setPitch] = useState(0);
    const [emotion, setEmotion] = useState('neutral');
    const [languageBoost, setLanguageBoost] = useState('Chinese,Yue');
    const [isLoading, setIsLoading] = useState(false);
    const [voiceTab, setVoiceTab] = useState('default'); // 'default' or 'cloned'
    const [error, setError] = useState('');
    try {
        
        // Default system voices
        const systemVoices = [
            { id: 'Wise_Woman', name: '智慧女性' },
            { id: 'Friendly_Person', name: '友善人士' },
            { id: 'Inspirational_girl', name: '鼓舞女孩' },
            { id: 'Deep_Voice_Man', name: '深沉男聲' },
            { id: 'Calm_Woman', name: '冷靜女性' },
            { id: 'Casual_Guy', name: '隨性男生' },
            { id: 'Lively_Girl', name: '活潑女孩' },
            { id: 'Patient_Man', name: '耐心男性' },
            { id: 'Young_Knight', name: '年輕騎士' },
            { id: 'Determined_Man', name: '堅定男性' },
            { id: 'Lovely_Girl', name: '可愛女孩' },
            { id: 'Decent_Boy', name: '端莊男孩' },
            { id: 'Imposing_Manner', name: '威嚴風格' },
            { id: 'Elegant_Man', name: '優雅男性' },
            { id: 'Abbess', name: '修女' },
            { id: 'Sweet_Girl_2', name: '甜美女孩2' },
            { id: 'Exuberant_Girl', name: '活力女孩' },
            { id: 'English_expressive_narrator', name: '表達力豐富的敘述者 (英語)' },
            { id: 'English_compelling_lady', name: '引人入勝的女士 (英語)' }
        ];

        // Emotions
        const emotions = [
            { id: 'happy', name: '開心' },
            { id: 'sad', name: '悲傷' },
            { id: 'angry', name: '憤怒' },
            { id: 'fearful', name: '恐懼' },
            { id: 'disgusted', name: '厭惡' },
            { id: 'surprised', name: '驚訝' },
            { id: 'neutral', name: '中性' }
        ];

        // Language boost options
        const languageOptions = [
            { id: 'Chinese,Yue', name: '廣東話' },
            { id: 'Chinese', name: '中文 (普通話)' },
            { id: 'English', name: '英文' },
            { id: 'Japanese', name: '日文' },
            { id: 'Korean', name: '韓文' },
            { id: 'French', name: '法文' },
            { id: 'German', name: '德文' },
            { id: 'Spanish', name: '西班牙文' },
            { id: 'Portuguese', name: '葡萄牙文' },
            { id: 'Italian', name: '義大利文' },
            { id: 'Russian', name: '俄文' },
            { id: 'Arabic', name: '阿拉伯文' },
            { id: 'Turkish', name: '土耳其文' },
            { id: 'Dutch', name: '荷蘭文' },
            { id: 'Ukrainian', name: '烏克蘭文' },
            { id: 'Vietnamese', name: '越南文' },
            { id: 'Indonesian', name: '印尼文' },
            { id: 'auto', name: '自動偵測' }
        ];

        useEffect(() => {
            // Set default voice if available
            if (systemVoices.length > 0) {
                setSelectedVoice(systemVoices[0].id);
            }
        }, []);

        const handleSubmit = async (e) => {
            e.preventDefault();
            setError('');
            
            if (!selectedVoice) {
                setError('請選擇一個語音');
                return;
            }
            
            setIsLoading(true);
            
            try {
                
                // Then generate the voice
                await onGenerate({
                    voiceId: selectedVoice,
                    speed: parseFloat(speed),
                    volume: parseFloat(volume),
                    pitch: parseInt(pitch),
                    emotion: emotion,
                    languageBoost: languageBoost,
                    id: id
                });
                
                onClose();
            } catch (error) {
                console.error('Voice generation error:', error);
                setError(error.message || '生成語音失敗，請稍後再試');
                setIsLoading(false);
            }
        };

        const handleVoiceChange = (e) => {
            setSelectedVoice(e.target.value);
        };

        const handleLanguageChange = (e) => {
            setLanguageBoost(e.target.value);
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-name="voice-settings-modal">
                <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" data-name="modal-content">
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold">
                                生成 AI 語音
                            </h2>
                            <button 
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                data-name="close-button"
                            >
                                <i className="fas fa-times text-xl"></i>
                            </button>
                        </div>
                        
                        {error && (
                            <div 
                                className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 rounded"
                                role="alert"
                                data-name="error-message"
                            >
                                <div className="flex">
                                    <i className="fas fa-exclamation-circle text-red-400 mr-3"></i>
                                    <p className="text-red-700">{error}</p>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="mb-6">
                                <div className="flex border-b border-gray-200 mb-4">
                                    <button 
                                        type="button"
                                        onClick={() => setVoiceTab('default')}
                                        className={`py-2 px-4 font-medium ${voiceTab === 'default' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                        data-name="default-voice-tab"
                                    >
                                        預設語音
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => setVoiceTab('cloned')}
                                        className={`py-2 px-4 font-medium ${voiceTab === 'cloned' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                        disabled={clonedVoices.length === 0}
                                        data-name="cloned-voice-tab"
                                    >
                                        已克隆語音
                                        {clonedVoices.length > 0 && (
                                            <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                                                {clonedVoices.length}
                                            </span>
                                        )}
                                    </button>
                                </div>

                                <label htmlFor="voiceSelect" className="block text-sm font-medium text-gray-700 mb-2">
                                    選擇語音
                                </label>
                                <select
                                    id="voiceSelect"
                                    value={selectedVoice}
                                    onChange={handleVoiceChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all"
                                    data-name="voice-select"
                                >
                                    <option value="">-- 請選擇語音 --</option>
                                    {voiceTab === 'default' ? (
                                        systemVoices.map(voice => (
                                            <option key={voice.id} value={voice.id}>
                                                {voice.name}
                                            </option>
                                        ))
                                    ) : (
                                        clonedVoices.map(voice => (
                                            <option key={voice.id} value={voice.id}>
                                                {voice.name || voice.id}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>

                            <div className="space-y-4 mb-6">
                                <div>
                                    <label htmlFor="languageBoost" className="block text-sm font-medium text-gray-700 mb-2">
                                        語言增強
                                    </label>
                                    <select
                                        id="languageBoost"
                                        value={languageBoost}
                                        onChange={handleLanguageChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all"
                                        data-name="language-boost-select"
                                    >
                                        {languageOptions.map(language => (
                                            <option key={language.id} value={language.id}>
                                                {language.name}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="mt-1 text-xs text-gray-500">
                                        增強對指定語言和方言的識別能力
                                    </p>
                                </div>

                                <div>
                                    <label htmlFor="speed" className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                                        <span>語速</span>
                                        <span className="text-blue-600">{speed}</span>
                                    </label>
                                    <input
                                        type="range"
                                        id="speed"
                                        min="0.5"
                                        max="2.0"
                                        step="0.1"
                                        value={speed}
                                        onChange={(e) => setSpeed(e.target.value)}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                        data-name="speed-slider"
                                    />
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                        <span>慢 0.5</span>
                                        <span>正常 1.0</span>
                                        <span>快 2.0</span>
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="volume" className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                                        <span>音量</span>
                                        <span className="text-blue-600">{volume}</span>
                                    </label>
                                    <input
                                        type="range"
                                        id="volume"
                                        min="0.1"
                                        max="10.0"
                                        step="0.1"
                                        value={volume}
                                        onChange={(e) => setVolume(e.target.value)}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                        data-name="volume-slider"
                                    />
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                        <span>小 0.1</span>
                                        <span>正常 1.0</span>
                                        <span>大 10.0</span>
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="pitch" className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                                        <span>音調</span>
                                        <span className="text-blue-600">{pitch}</span>
                                    </label>
                                    <input
                                        type="range"
                                        id="pitch"
                                        min="-12"
                                        max="12"
                                        step="1"
                                        value={pitch}
                                        onChange={(e) => setPitch(e.target.value)}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                        data-name="pitch-slider"
                                    />
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                        <span>低 -12</span>
                                        <span>正常 0</span>
                                        <span>高 12</span>
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="emotion" className="block text-sm font-medium text-gray-700 mb-2">
                                        情緒
                                    </label>
                                    <select
                                        id="emotion"
                                        value={emotion}
                                        onChange={(e) => setEmotion(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all"
                                        data-name="emotion-select"
                                    >
                                        {emotions.map(item => (
                                            <option key={item.id} value={item.id}>
                                                {item.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            
                            <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                                <div className="flex items-center">
                                    <i className="fas fa-coins text-yellow-500 mr-2"></i>
                                    <p className="text-yellow-700">此操作將消耗 1 點數</p>
                                </div>
                            </div>

                            <div className="border-t pt-4 flex justify-end space-x-4">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                    disabled={isLoading}
                                    data-name="cancel-button"
                                >
                                    取消
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                                    disabled={isLoading || !selectedVoice}
                                    data-name="generate-button"
                                >
                                    {isLoading ? (
                                        <Fragment>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                            生成中...
                                        </Fragment>
                                    ) : (
                                        <Fragment>
                                            <i className="fas fa-volume-up mr-2"></i>
                                            生成語音
                                        </Fragment>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    } catch (error) {
        console.error('VoiceSettingsModal component error:', error);
        reportError(error);
        return null;
    }
}

export default VoiceSettingsModal;